/**
 * Tiktok 发布记录本地存储（按店铺隔离）。
 * 主态由「同步更新」调 Get Product 的 audit.status 回写。
 */

import {
  applyDerivedPublishStatuses,
  normalizePublishRecordStatusLabel,
  PUBLISH_RECORD_STATUS,
} from '@/tiktok-platform/utils/tiktokPublishRecordStatus';
import {
  extractTiktokPublishedSkus,
  extractTiktokPublishedSpu,
} from '@/tiktok-platform/utils/tiktokPublishResult';

const IDB_NAME = 'ipe-tiktok-publish-records';
const IDB_STORE = 'records';
const IDB_VERSION = 1;
const INDEX_PREFIX = 'ipe_tiktok_publish_records_index_';

function indexKey(shopId) {
  return `${INDEX_PREFIX}${String(shopId || '0')}`;
}

function makeId() {
  return `tpr-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function readIndex(shopId) {
  try {
    const raw = localStorage.getItem(indexKey(shopId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeIndex(shopId, items) {
  try {
    localStorage.setItem(indexKey(shopId), JSON.stringify(items));
  } catch {
    /* quota */
  }
}

function openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('indexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(IDB_NAME, IDB_VERSION);
    req.onerror = () => reject(req.error || new Error('indexedDB open failed'));
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) {
        const store = db.createObjectStore(IDB_STORE, { keyPath: 'id' });
        store.createIndex('shopId', 'shopId', { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
  });
}

function idbPut(record) {
  return openDb().then((db) => new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readwrite');
    tx.oncomplete = () => {
      db.close();
      resolve(record);
    };
    tx.onerror = () => {
      db.close();
      reject(tx.error || new Error('indexedDB put failed'));
    };
    tx.objectStore(IDB_STORE).put(record);
  }));
}

function idbGet(id) {
  return openDb().then((db) => new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readonly');
    const req = tx.objectStore(IDB_STORE).get(String(id));
    req.onsuccess = () => {
      db.close();
      resolve(req.result || null);
    };
    req.onerror = () => {
      db.close();
      reject(req.error || new Error('indexedDB get failed'));
    };
  }));
}

function idbDelete(ids) {
  const idList = (Array.isArray(ids) ? ids : [ids]).map(String).filter(Boolean);
  if (!idList.length) return Promise.resolve(0);
  return openDb().then((db) => new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readwrite');
    let deleted = 0;
    tx.oncomplete = () => {
      db.close();
      resolve(deleted);
    };
    tx.onerror = () => {
      db.close();
      reject(tx.error || new Error('indexedDB delete failed'));
    };
    const store = tx.objectStore(IDB_STORE);
    idList.forEach((id) => {
      store.delete(id);
      deleted += 1;
    });
  }));
}

function imageUrlOf(image) {
  if (!image) return '';
  return String(
    image.tiktokUrl
    || image.publicUrl
    || image.ipeUrl
    || image.previewUrl
    || image.url
    || '',
  ).trim();
}

function firstSupplierCode(form) {
  const fromSkc = (form?.skcList || [])
    .map((skc) => String(skc?.supplierCode || '').trim())
    .find(Boolean);
  return fromSkc || String(form?.supplierCode || '').trim();
}

/** 平台雪花 ID 用字符串；超出 Number 安全整数时 Number() 会丢精度 */
function asPlatformId(v) {
  const t = String(v ?? '').trim();
  if (!t || t === '0' || t === 'null' || t === 'undefined') return '';
  return t;
}

function salesAttrsOfSku(sku) {
  if (Array.isArray(sku?.sales_attributes)) return sku.sales_attributes;
  if (Array.isArray(sku?.salesAttributes)) return sku.salesAttributes;
  return [];
}

function sizeLabelFromPlatformSku(sku) {
  const attrs = salesAttrsOfSku(sku);
  const sizeAttr = attrs.find((a) => /size|尺码|尺寸/i.test(String(a?.name || '')))
    || attrs.find((a) => !/color|colour|颜色|色彩/i.test(String(a?.name || '')) && attrs.indexOf(a) > 0)
    || null;
  return String(sizeAttr?.value_name || sizeAttr?.valueName || '').trim();
}

function colorMatchesSkc(sku, skc) {
  const colorVid = asPlatformId(skc?.colorValueId);
  const colorName = String(skc?.colorLabel || skc?.colorExtraValue || '').trim().toLowerCase();
  if (!colorVid && !colorName) return true;
  return salesAttrsOfSku(sku).some((a) => {
    const vid = asPlatformId(a?.value_id || a?.valueId);
    const vname = String(a?.value_name || a?.valueName || '').trim().toLowerCase();
    return (colorVid && vid === colorVid) || (colorName && vname === colorName);
  });
}

function skcHasRichMerchantSkus(skc) {
  return Array.isArray(skc?.merchantSkus)
    && skc.merchantSkus.some((m) => m && (m.sizeLabel || m.supplierSku || m.merchantSku));
}

/**
 * 规格行明细：尺码 ↔ 商家SKU(seller_sku) ↔ 平台 SKU ID。
 * TTS Create Product 无 SKC：用表单色块行 + 平台 skus 按 seller_sku / 颜色销售属性对齐。
 */
function buildMerchantSkuRowsForSkc(skc, platformSkus = []) {
  const ttsSkus = Array.isArray(platformSkus) ? platformSkus : [];
  const formRows = Array.isArray(skc?.skuRows)
    ? skc.skuRows
    : (Array.isArray(skc?.skuList) ? skc.skuList : []);
  const usedPlatform = new Set();

  const matchPlatform = (row) => {
    const sellerSku = String(row?.supplierSku || row?.sellerSku || row?.seller_sku || '').trim();
    const sizeLabel = String(row?.sizeLabel || row?.sizeName || row?.skuSize || '').trim();
    const sizeVid = asPlatformId(row?.sizeValueId);
    let productSkuId = asPlatformId(row?.platformSkuId || row?.skuCode || row?.skuId || row?.id);
    if (productSkuId && ttsSkus.some((s) => asPlatformId(s?.id || s?.sku_id) === productSkuId)) {
      usedPlatform.add(productSkuId);
      return { sizeLabel, supplierSku: sellerSku, productSkuId };
    }
    const hit = ttsSkus.find((sku) => {
      const id = asPlatformId(sku?.id || sku?.sku_id || sku?.skuId);
      if (!id || usedPlatform.has(id)) return false;
      const ss = String(sku?.seller_sku || sku?.sellerSku || '').trim();
      if (sellerSku && ss === sellerSku) return true;
      if (!colorMatchesSkc(sku, skc)) return false;
      const platSize = sizeLabelFromPlatformSku(sku);
      const platSizeVid = (() => {
        const attrs = salesAttrsOfSku(sku);
        const sizeAttr = attrs.find((a) => /size|尺码|尺寸/i.test(String(a?.name || '')));
        return asPlatformId(sizeAttr?.value_id || sizeAttr?.valueId);
      })();
      if (sizeVid && platSizeVid && sizeVid === platSizeVid) return true;
      if (sizeLabel && platSize && sizeLabel.toLowerCase() === platSize.toLowerCase()) return true;
      return false;
    });
    if (hit) {
      productSkuId = asPlatformId(hit.id || hit.sku_id || hit.skuId);
      usedPlatform.add(productSkuId);
      return {
        sizeLabel: sizeLabel || sizeLabelFromPlatformSku(hit),
        supplierSku: sellerSku || String(hit.seller_sku || hit.sellerSku || '').trim(),
        productSkuId,
      };
    }
    return { sizeLabel, supplierSku: sellerSku, productSkuId: productSkuId || '' };
  };

  if (formRows.length) {
    return formRows.map((row) => {
      const mapped = matchPlatform(row);
      return {
        sizeLabel: mapped.sizeLabel || '—',
        supplierSku: mapped.supplierSku,
        merchantSku: mapped.supplierSku,
        productSkuId: mapped.productSkuId,
        platformSkuId: mapped.productSkuId,
      };
    });
  }

  // 无表单 SKU 行：按颜色从平台 skus 拆尺码
  return ttsSkus
    .filter((sku) => colorMatchesSkc(sku, skc))
    .map((sku) => {
      const id = asPlatformId(sku?.id || sku?.sku_id || sku?.skuId);
      const supplierSku = String(sku?.seller_sku || sku?.sellerSku || '').trim();
      return {
        sizeLabel: sizeLabelFromPlatformSku(sku) || '—',
        supplierSku,
        merchantSku: supplierSku,
        productSkuId: id,
        platformSkuId: id,
      };
    })
    .filter((row) => row.productSkuId || row.supplierSku);
}

function buildSkcItemsFromForm(form = {}, platformSkcList = [], {
  platformSkus = [],
  initialStatus = PUBLISH_RECORD_STATUS.PENDING_REVIEW,
} = {}) {
  const formSkcs = Array.isArray(form.skcList) ? form.skcList : [];
  const platformByIndex = Array.isArray(platformSkcList) ? platformSkcList : [];
  const ttsSkus = Array.isArray(platformSkus) ? platformSkus : [];
  if (formSkcs.length) {
    return formSkcs.map((skc, index) => {
      const plat = platformByIndex[index] || {};
      const skuList = Array.isArray(plat.productSkuList)
        ? plat.productSkuList
        : (Array.isArray(plat.skuList) ? plat.skuList : []);
      const fromLegacySkc = skuList
        .map((s) => asPlatformId(s?.productSkuId ?? s?.skuId ?? s?.id ?? s))
        .filter(Boolean);
      const merchantSkus = buildMerchantSkuRowsForSkc(skc, ttsSkus);
      const fromTts = merchantSkus.map((m) => m.productSkuId).filter(Boolean);
      const formSkuIds = (Array.isArray(skc.skuList) ? skc.skuList : [])
        .map((s) => asPlatformId(s?.productSkuId ?? s?.skuId ?? s?.id))
        .filter(Boolean);
      const ids = fromTts.length ? fromTts : (fromLegacySkc.length ? fromLegacySkc : formSkuIds);
      const skcName = String(
        skc.colorLabel
        || skc.colorExtraValue
        || skc.skcName
        || skc.supplierCode
        || '',
      ).trim();
      const firstMerchant = merchantSkus.find((m) => m.supplierSku)?.supplierSku || '';
      return {
        colorLabel: skc.colorLabel || skc.colorExtraValue || '',
        skcName,
        productSkcId: null,
        productSkuIds: ids,
        imageUrl: imageUrlOf(skc.mainImage),
        merchantSkus: merchantSkus.length
          ? merchantSkus
          : ids.map((id) => ({ productSkuId: id, platformSkuId: id, sizeLabel: '—', supplierSku: '' })),
        supplierCode: String(skc.supplierCode || firstMerchant || '').trim(),
        status: initialStatus,
        statusLabel: initialStatus,
      };
    });
  }
  if (ttsSkus.length) {
    // 无表单色块：按颜色拆主规格
    const byColor = new Map();
    ttsSkus.forEach((sku) => {
      const attrs = salesAttrsOfSku(sku);
      const colorAttr = attrs.find((a) => /color|colour|颜色|色彩/i.test(String(a?.name || '')))
        || attrs[0];
      const key = asPlatformId(colorAttr?.value_id || colorAttr?.valueId)
        || String(colorAttr?.value_name || colorAttr?.valueName || 'default').trim();
      if (!byColor.has(key)) {
        byColor.set(key, {
          colorLabel: String(colorAttr?.value_name || colorAttr?.valueName || '').trim(),
          skcName: String(colorAttr?.value_name || colorAttr?.valueName || '').trim(),
          productSkcId: null,
          productSkuIds: [],
          imageUrl: '',
          merchantSkus: [],
          supplierCode: '',
          status: initialStatus,
          statusLabel: initialStatus,
        });
      }
      const node = byColor.get(key);
      const id = asPlatformId(sku?.id || sku?.sku_id);
      const supplierSku = String(sku?.seller_sku || sku?.sellerSku || '').trim();
      if (id) node.productSkuIds.push(id);
      node.merchantSkus.push({
        sizeLabel: sizeLabelFromPlatformSku(sku) || '—',
        supplierSku,
        merchantSku: supplierSku,
        productSkuId: id,
        platformSkuId: id,
      });
      if (!node.supplierCode && supplierSku) node.supplierCode = supplierSku;
    });
    return Array.from(byColor.values());
  }
  return platformByIndex.map((plat) => {
    const skuList = Array.isArray(plat.productSkuList)
      ? plat.productSkuList
      : (Array.isArray(plat.skuList) ? plat.skuList : []);
    const ids = skuList
      .map((s) => asPlatformId(s?.productSkuId ?? s?.skuId ?? s))
      .filter(Boolean);
    return {
      colorLabel: '',
      skcName: String(plat.productSkcId || plat.skcId || '').trim(),
      productSkcId: plat.productSkcId ?? plat.skcId ?? null,
      productSkuIds: ids,
      imageUrl: '',
      merchantSkus: ids.map((id) => ({ productSkuId: id, platformSkuId: id, sizeLabel: '—', supplierSku: '' })),
      supplierCode: String(plat.extCode || plat.supplierCode || '').trim(),
      status: initialStatus,
      statusLabel: initialStatus,
    };
  });
}

/**
 * 列表展示 enrichment：用 formSnapshot / platformRaw 补齐尺码与商家SKU（兼容旧记录）。
 */
export function enrichPublishRecordForDisplay(record) {
  if (!record || typeof record !== 'object') return record;
  const form = record.formSnapshot && typeof record.formSnapshot === 'object'
    ? record.formSnapshot
    : null;
  const fromCreate = extractTiktokPublishedSkus(record.platformRaw || record.platformRes);
  const fromGet = Array.isArray(record.platformProductRaw?.skus)
    ? record.platformProductRaw.skus
    : [];
  const platformSkus = fromCreate.length ? fromCreate : fromGet;
  const hasRichMerchant = (Array.isArray(record.skcItems) ? record.skcItems : [])
    .some((skc) => skcHasRichMerchantSkus(skc));
  if (hasRichMerchant) return record;
  if (!form && !platformSkus.length) return record;
  const initialStatus = normalizePublishRecordStatusLabel(
    record.statusLabel || record.status || PUBLISH_RECORD_STATUS.PENDING_REVIEW,
  );
  const nextSkc = buildSkcItemsFromForm(form || {}, [], {
    platformSkus,
    initialStatus,
  });
  if (!nextSkc.length) return record;
  return {
    ...record,
    skcItems: nextSkc,
    productSkuIds: [...new Set(nextSkc.flatMap((s) => s.productSkuIds || []))],
    categoryLabel: record.categoryLabel
      || String(form?.categoryPathLabel || form?.categoryLabel || '').trim(),
    title: record.title || String(form?.title || '').trim(),
  };
}

function toListItem(record) {
  return {
    id: record.id,
    shopId: record.shopId,
    title: record.title || '',
    supplierCode: record.supplierCode || '',
    platformSpuName: record.platformSpuName || record.platformProductId || '',
    platformProductId: record.platformProductId || record.platformSpuName || '',
    categoryLabel: record.categoryLabel || '',
    action: record.action || 'publish',
    source: record.source || 'wizard',
    typeLabel: record.typeLabel || (record.action === 'edit' ? '编辑' : '新增'),
    status: record.status || PUBLISH_RECORD_STATUS.PENDING_REVIEW,
    statusLabel: record.statusLabel || record.status || PUBLISH_RECORD_STATUS.PENDING_REVIEW,
    skcItems: Array.isArray(record.skcItems) ? record.skcItems : [],
    productSkuIds: Array.isArray(record.productSkuIds) ? record.productSkuIds : [],
    draftId: record.draftId || null,
    remark: record.remark || '',
    platformAuditStatus: record.platformAuditStatus || null,
    platformProductStatus: record.platformProductStatus || null,
    platformStatusSyncedAt: record.platformStatusSyncedAt || null,
    createdAt: record.createdAt || null,
    publishedAt: record.publishedAt || null,
    finishedAt: record.finishedAt || null,
    updatedAt: record.updatedAt || null,
    canDelete: true,
    canRevoke: false,
  };
}

async function hydratePublishRecordListRow(shopId, row) {
  if (!row || !row.id) return applyDerivedPublishStatuses(row);
  const skcItems = Array.isArray(row.skcItems) ? row.skcItems : [];
  const rich = skcItems.length > 0 && skcItems.every(skcHasRichMerchantSkus);
  if (rich && row.platformProductStatus) {
    return applyDerivedPublishStatuses(row);
  }
  const body = await getTiktokLocalPublishRecord(shopId, row.id);
  if (!body) return applyDerivedPublishStatuses(row);
  const merged = {
    ...toListItem(body),
    formSnapshot: body.formSnapshot || null,
    platformRaw: body.platformRaw || null,
    platformProductRaw: body.platformProductRaw || null,
  };
  return applyDerivedPublishStatuses(enrichPublishRecordForDisplay(merged));
}

async function persistRecord(record) {
  try {
    await idbPut(record);
  } catch {
    try {
      localStorage.setItem(`ipe_tiktok_publish_record_body_${record.id}`, JSON.stringify(record));
    } catch {
      /* ignore */
    }
  }
  const shopId = record.shopId;
  const index = readIndex(shopId);
  const nextItem = toListItem(record);
  const idx = index.findIndex((row) => String(row.id) === String(record.id));
  if (idx >= 0) index[idx] = { ...index[idx], ...nextItem };
  else index.unshift(nextItem);
  writeIndex(shopId, index.slice(0, 500));
  return nextItem;
}

/** 列出可同步审核态的记录（需有 platformSpuName / product_id） */
export function listTiktokRefreshablePublishRecords(shopId) {
  return readIndex(shopId).filter((row) => String(row?.platformSpuName || '').trim());
}

/**
 * 用平台 Get Product 结果回写本地记录状态。
 * @param {object} patch
 * @param {string} patch.statusLabel
 * @param {string} [patch.remark]
 * @param {string} [patch.platformAuditStatus]
 * @param {string} [patch.platformProductStatus]
 * @param {object} [patch.platformProductRaw]
 */
export async function updateTiktokLocalPublishRecordPlatformStatus(shopId, recordId, patch = {}) {
  const existing = await getTiktokLocalPublishRecord(shopId, recordId);
  if (!existing) return null;
  const label = normalizePublishRecordStatusLabel(
    patch.statusLabel || patch.status || PUBLISH_RECORD_STATUS.PENDING_REVIEW,
  );
  const now = new Date().toISOString();
  const skcItems = (Array.isArray(existing.skcItems) ? existing.skcItems : []).map((skc) => ({
    ...skc,
    status: label,
    statusLabel: label,
  }));
  const finished = label === PUBLISH_RECORD_STATUS.APPROVED
    || label === PUBLISH_RECORD_STATUS.LISTED
    || label === PUBLISH_RECORD_STATUS.REJECTED;
  const next = {
    ...existing,
    status: label,
    statusLabel: label,
    skcItems,
    remark: patch.remark != null ? String(patch.remark) : (existing.remark || ''),
    platformAuditStatus: patch.platformAuditStatus != null
      ? String(patch.platformAuditStatus)
      : (existing.platformAuditStatus || null),
    platformProductStatus: patch.platformProductStatus != null
      ? String(patch.platformProductStatus)
      : (existing.platformProductStatus || null),
    platformProductRaw: patch.platformProductRaw != null
      ? patch.platformProductRaw
      : (existing.platformProductRaw || null),
    platformStatusSyncedAt: now,
    finishedAt: finished ? (existing.finishedAt || now) : existing.finishedAt,
    updatedAt: now,
  };
  return persistRecord(next);
}

/**
 * 发品成功后写入本地发布记录。
 * TTS Create Product 无 SKC/SPU：platformSpuName = product_id；SKU 取 data.skus[].id。
 * listed=true（默认，Create+Activate）初始为「已上架」；若走审核再同步为待审核/已通过。
 */
export async function createTiktokLocalPublishRecord(shopId, {
  form = {},
  platformRes = null,
  publishedSpu = '',
  draftId = null,
  action = 'publish',
  /** Create Product 后是否已 Activate；true → 已上架 */
  listed = true,
} = {}) {
  const id = makeId();
  const now = new Date().toISOString();
  const result = platformRes?.result && typeof platformRes.result === 'object'
    ? platformRes.result
    : (platformRes?.info && typeof platformRes.info === 'object' ? platformRes.info : platformRes);
  const platformSkcList = Array.isArray(result?.productSkcList)
    ? result.productSkcList
    : (Array.isArray(result?.productSkcIdList) ? result.productSkcIdList.map((v) => ({ productSkcId: v })) : []);
  const platformSkus = extractTiktokPublishedSkus(platformRes);
  const initialStatus = listed
    ? PUBLISH_RECORD_STATUS.LISTED
    : PUBLISH_RECORD_STATUS.PENDING_REVIEW;
  const skcItems = buildSkcItemsFromForm(form, platformSkcList, { platformSkus, initialStatus });
  const productSkuIds = [...new Set(skcItems.flatMap((s) => s.productSkuIds || []))];
  const productId = String(
    publishedSpu
    || extractTiktokPublishedSpu(platformRes)
    || result?.productId
    || result?.product_id
    || form?.spuName
    || '',
  ).trim();
  const record = {
    id,
    shopId: String(shopId),
    title: String(form?.title || form?.productName || productId || '未命名商品').trim(),
    supplierCode: firstSupplierCode(form),
    // 历史字段名 platformSpuName：TTS 存 product_id
    platformSpuName: productId,
    platformProductId: productId,
    categoryLabel: String(form?.categoryLabel || form?.categoryPathLabel || '').trim(),
    action: action === 'edit' ? 'edit' : 'publish',
    source: 'wizard',
    typeLabel: action === 'edit' ? '编辑' : '新增',
    status: initialStatus,
    statusLabel: initialStatus,
    skcItems,
    productSkuIds,
    draftId: draftId != null ? String(draftId) : null,
    formSnapshot: form && typeof form === 'object' ? form : {},
    platformRaw: platformRes || null,
    platformProductStatus: listed ? 'ACTIVATE' : null,
    remark: '',
    createdAt: now,
    publishedAt: now,
    finishedAt: listed ? now : null,
    updatedAt: now,
  };
  try {
    await idbPut(record);
  } catch {
    try {
      localStorage.setItem(`ipe_tiktok_publish_record_body_${id}`, JSON.stringify(record));
    } catch {
      /* ignore */
    }
  }
  const index = readIndex(shopId);
  index.unshift(toListItem(record));
  writeIndex(shopId, index.slice(0, 500));
  return toListItem(record);
}

export async function getTiktokLocalPublishRecord(shopId, recordId) {
  const id = String(recordId || '');
  if (!id) return null;
  let record = null;
  try {
    record = await idbGet(id);
  } catch {
    record = null;
  }
  if (!record) {
    try {
      const raw = localStorage.getItem(`ipe_tiktok_publish_record_body_${id}`);
      if (raw) record = JSON.parse(raw);
    } catch {
      record = null;
    }
  }
  if (!record || String(record.shopId) !== String(shopId)) return null;
  return record;
}

/** 按 TTS product_id 查找完整本地发布记录（含 formSnapshot） */
export async function findTiktokLocalPublishRecordByProductId(shopId, productId) {
  const pid = String(productId || '').trim();
  if (!shopId || !pid) return null;
  const hit = readIndex(shopId).find((row) => {
    const id = String(row?.platformSpuName || row?.platformProductId || row?.productId || '').trim();
    return id && id === pid;
  });
  if (!hit?.id) return null;
  return getTiktokLocalPublishRecord(shopId, hit.id);
}

/**
 * 产品信息列表用：product_id → 本地发布记录的创建/发布时间。
 * 仅读 index（含 createdAt / publishedAt），不扫 IDB 全文。
 * @returns {Map<string, { createdAt: string|null, publishedAt: string|null }>}
 */
export function mapTiktokLocalPublishRecordTimesByProductId(shopId) {
  const map = new Map();
  if (!shopId) return map;
  readIndex(shopId).forEach((row) => {
    const pid = String(row?.platformSpuName || row?.platformProductId || row?.productId || '').trim();
    if (!pid) return;
    const createdAt = row?.createdAt || null;
    const publishedAt = row?.publishedAt || row?.finishedAt || null;
    if (!createdAt && !publishedAt) return;
    const prev = map.get(pid);
    if (!prev) {
      map.set(pid, { createdAt, publishedAt });
      return;
    }
    map.set(pid, {
      createdAt: prev.createdAt || createdAt,
      publishedAt: prev.publishedAt || publishedAt,
    });
  });
  return map;
}

/**
 * @param {object} [options]
 * @param {Array} [options.reviewItems] 兼容旧参数，发布记录不再用于状态叠加
 */
export async function listTiktokLocalPublishRecords(shopId, params = {}, { reviewItems = [] } = {}) {
  const page = Math.max(1, Number(params.page) || 1);
  const pageSize = Math.min(100, Math.max(1, Number(params.pageSize) || 20));
  const statusFilter = String(params.status || '').trim();
  const titleQ = String(params.title || '').trim().toLowerCase();
  const supplierQ = String(params.supplierCode || '').trim().toLowerCase();
  const keywords = String(params.keywords || '').trim().toLowerCase();
  const idType = String(params.idType || 'skc').trim();
  const keywordList = keywords
    ? keywords.split(/[,，\s]+/).map((s) => s.trim().toLowerCase()).filter(Boolean)
    : [];

  void reviewItems;
  const indexRows = readIndex(shopId);
  const allDerived = await Promise.all(
    indexRows.map((row) => hydratePublishRecordListRow(shopId, row)),
  );
  // 规格明细补齐后写回 index，避免下次再扫 IDB
  const nextIndex = allDerived.map((row) => toListItem(row));
  const indexChanged = nextIndex.some((row, i) => {
    const prev = indexRows[i];
    if (!prev || String(prev.id) !== String(row.id)) return true;
    if (String(prev.statusLabel || '') !== String(row.statusLabel || '')) return true;
    if (String(prev.platformProductStatus || '') !== String(row.platformProductStatus || '')) return true;
    const prevRich = (Array.isArray(prev.skcItems) ? prev.skcItems : []).every(skcHasRichMerchantSkus);
    const nextRich = (Array.isArray(row.skcItems) ? row.skcItems : []).every(skcHasRichMerchantSkus);
    return !prevRich && nextRich;
  });
  if (indexChanged) writeIndex(shopId, nextIndex);

  const statusCounts = {
    all: allDerived.length,
    [PUBLISH_RECORD_STATUS.PENDING_REVIEW]: 0,
    [PUBLISH_RECORD_STATUS.APPROVED]: 0,
    [PUBLISH_RECORD_STATUS.LISTED]: 0,
    [PUBLISH_RECORD_STATUS.REJECTED]: 0,
  };
  allDerived.forEach((row) => {
    const s = String(row.statusLabel || row.status);
    if (Object.prototype.hasOwnProperty.call(statusCounts, s)) {
      statusCounts[s] += 1;
    }
  });

  let items = allDerived;
  if (statusFilter) {
    items = items.filter((row) => String(row.statusLabel || row.status) === statusFilter);
  }
  if (titleQ) {
    items = items.filter((row) => String(row.title || '').toLowerCase().includes(titleQ));
  }
  if (supplierQ) {
    items = items.filter((row) => {
      if (String(row.supplierCode || '').toLowerCase().includes(supplierQ)) return true;
      return (Array.isArray(row.skcItems) ? row.skcItems : []).some((skc) => (
        String(skc?.supplierCode || '').toLowerCase().includes(supplierQ)
      ));
    });
  }
  if (keywordList.length) {
    items = items.filter((row) => {
      const hay = idType === 'spu'
        ? String(row.platformSpuName || '').toLowerCase()
        : idType === 'sku'
          ? (row.productSkuIds || []).map(String).join(' ').toLowerCase()
          : (Array.isArray(row.skcItems) ? row.skcItems : [])
            .map((s) => String(s.skcName || '')).join(' ').toLowerCase();
      return keywordList.some((k) => hay.includes(k));
    });
  }

  const total = items.length;
  const start = (page - 1) * pageSize;
  const pageItems = items.slice(start, start + pageSize);

  return {
    items: pageItems,
    content: pageItems,
    total,
    totalElements: total,
    page,
    pageSize,
    statusCounts,
    neverSynced: false,
  };
}

export async function batchDeleteTiktokLocalPublishRecords(shopId, ids = []) {
  const idList = (Array.isArray(ids) ? ids : []).map(String).filter(Boolean);
  if (!idList.length) return { deleted: 0 };
  await idbDelete(idList);
  idList.forEach((id) => {
    try {
      localStorage.removeItem(`ipe_tiktok_publish_record_body_${id}`);
    } catch {
      /* ignore */
    }
  });
  const idSet = new Set(idList);
  writeIndex(shopId, readIndex(shopId).filter((row) => !idSet.has(String(row.id))));
  return { deleted: idList.length, ok: true };
}
